
import { Component, Mixins, Prop } from "vue-property-decorator";
import {
  DocumentSummary,
  DocumentSummaryHistory,
  DocumentTypeKinds
} from "../../../domain/document";
import { DomainRegistry } from "../../../domain";
import {
  ModelEditableMixin,
  ModalPresentableMixin,
} from "../../system/mixins";
import { FormatValidatableMixin } from "../../format/mixins";
import { DepartmentGroup } from "../../../domain/department";
import { PostPrintingApplicationLauncher } from "../helpers";
import { PostDeletionApplicationLauncher } from "../editor/operators/PostDeletionApplicationLauncher";

@Component
export default class DocumentSummaryEditableMixin extends Mixins(ModelEditableMixin, ModalPresentableMixin, FormatValidatableMixin) {
  data() {
    return {
      isProcessing: false,
    };
  }
  editingSource: DocumentSummary;
  readPropertyOrDefault: <T>(reader: (source: DocumentSummary) => T, defaultValue: T) => T;
  writePropertyWhenValid: (writer: (source: DocumentSummary) => void) => void;
  isProcessing: boolean;

  @Prop()
  archive: DocumentSummaryHistory;

  get isDocumentEnable(): boolean {
    return (this.editingSource !== null);
  }

  get isEditable(): boolean {
    return this.$store.getters.userAuthority.documentEditable;
  }

  get isDeletable(): boolean {
    return this.$store.getters.userAuthority.documentDeletable;
  }

  get isPrintable(): boolean {
    return this.$store.getters.userAuthority.documentPrintable;
  }

  get isQuotable(): boolean {
    return this.$configuration.behavior.document.list.menu.quote.isEnable;
  }

  get isAutomaticallyQuotable(): boolean {
    return this.$configuration.behavior.document.list.menu.quoteAutomatically.isEnable;
  }

  get isCopyable(): boolean {
    return this.$configuration.behavior.document.list.menu.copy.isEnable;
  }

  get isDocumentReadable(): boolean {
    return this.readPropertyOrDefault(s => s.isReadable, false);
  }

  get isDocumentNormal(): boolean {
    return this.readPropertyOrDefault(s => (s.documentType === DocumentTypeKinds.Normal), false);
  }

  get isDocumentRemovable(): boolean {
    return this.isDocumentNormal
      && this.readPropertyOrDefault(s => s.isRemovable, false);
  }

  get isDocumentWritable(): boolean {
    return this.isDocumentNormal
      && this.readPropertyOrDefault(s => s.isWritable, false);
  }

  get isNoteWritable(): boolean {
    // 編集不可・削除された状態であっても文書メモは編集可能とする
    return this.isDocumentEnable;
  }

  get isDocumentDeleted(): boolean {
    return this.readPropertyOrDefault(s => s.isDeleted, false);
  }

  get isDocumentFixed(): boolean {
    return this.readPropertyOrDefault(s => s.isFixed, false);
  }

  get isDocumentPrintable(): boolean {
    return this.isArchiveSelected
      ? this.archive.isPrintable
      : this.readPropertyOrDefault(s => s.isPrintable, true);
  }

  get isArchiveSelected(): boolean {
    return (this.archive !== null);
  }

  get archiveId(): number {
    return this.isArchiveSelected
      ? this.archive.archiveId
      : null;
  }

  get documentId(): number {
    return this.readPropertyOrDefault(s => s.id, undefined);
  }

  get patientId(): string {
    return this.readPropertyOrDefault(s => s.patientNo, undefined);
  }

  get formatOriginalId(): number {
    return this.readPropertyOrDefault(s => s.formatOriginalId, undefined);
  }

  get printSource(): string {
    return this.isArchiveSelected
      ? this.archive.printSource.source
      : this.readPropertyOrDefault(s => s.printSource.source, null);
  }

  get defaultCopies(): number {
    return this.isArchiveSelected
      ? this.archive.defaultCopies
      : this.readPropertyOrDefault(s => s.defaultCopies, 1);
  }

  get printCopies(): number {
    return this.$configuration.behavior.document.list.printing.shouldPrintDefaultCopies
      ? this.defaultCopies
      : 1;
  }

  get isDocumentQuotable(): boolean {
    return this.readPropertyOrDefault(s => s.isQuotable, true);
  }

  get isDocumentUnmanaged(): boolean {
    return this.readPropertyOrDefault(s => s.isUnmanaged, false);
  }

  get alternativeFormatOriginalId(): number {
    return this.readPropertyOrDefault(s => s.alternativeFormatOriginalId, null);
  }

  get isVirtual(): boolean {
    return this.readPropertyOrDefault(s => s.isVirtual, false);
  }

  get isDocumentCopyable(): boolean {
    if (this.isDocumentUnmanaged || !this.isDocumentQuotable) { return false; }
    // 厳密に施設間の参照管理するときにコピーできてしまうと都合が悪いことが多そう
    // 現状では相澤病院だけの機能なので、言われるまで施設をまたがったコピー機能は使用不可としておく
    if (this.$store.getters.isDepartmentGroupScopeEnable) {
      const loginDepartmentGroup = this.$store.getters.departmentGroup as DepartmentGroup;
      if (!loginDepartmentGroup || !this.departmentId) { return false; }
      return loginDepartmentGroup.departments.some(d => d.id === this.departmentId);
    }
    return true;
  }

  get departmentId(): string {
    return this.readPropertyOrDefault(s => s.department.id, null);
  }

  editNewQuotedDocument(): void {
    if (!this.isDocumentReadable) {
      return;
    }
    this.$logger.info("文書を引用・転記して作成します。");

    // 別Windowで開く.(完全SPAにするとメモリリーク発生による問題が顕在化しやすくなる恐れがあるため.)
    this.$openWindow(
      "/EditNewQuotedDocument",
      {
        patientNo: this.patientId,
        quotedDocumentId: this.documentId.toString(),
        defaultFormatId: this.alternativeFormatOriginalId ? this.alternativeFormatOriginalId.toString() : this.formatOriginalId.toString(),
      }
    );
  }

  /**
   * 文書と同じフォーマットで引用文書新規記載画面を表示する
   * フォーマットが使えない状態になっていると記載できないため、事前にチェックする
   */
  async editNewAutomaticallyQuotedDocument(): Promise<void> {
    if (!this.isDocumentReadable) {
      return;
    }

    const originalId = this.alternativeFormatOriginalId ? this.alternativeFormatOriginalId : this.formatOriginalId;
    try {
      const repository = DomainRegistry.format.getFormatRepository();
      const status = await repository.getFormatStatus(originalId);
      if (status.deleted) {
        return this.showConfirmDialogAndOpenEditNewQuotedDocument("確認", "この文書の" + this.$wording.format.name + "は削除されています。", "別の" + this.$wording.format.name + "を選択する");
      }
      if (status.expired) {
        return this.showConfirmDialogAndOpenEditNewQuotedDocument("確認", "この文書の" + this.$wording.format.name + "は有効期限が切れています。", "別の" + this.$wording.format.name + "を選択する");
      }
      const reference = await repository.getFormatReference(originalId);
      if (!this.isExpectedWriteMethodType(reference, false)) {
        return this.showConfirmDialogAndOpenEditNewQuotedDocument("確認", "この文書の" + this.$wording.format.name + "は記載方法が指定されています。", this.$wording.format.name + "を選択する");
      }
    } catch (e) {
      this.$error("エラー", this.$wording.format.name + "の状態確認中にエラーが発生しました。", e);
      this.editNewQuotedDocument();
      return;
    }

    this.$logger.info("文書を引用して作成します。");

    this.$openWindow(
      "/EditNewQuotedDocument",
      {
        patientNo: this.patientId,
        quotedDocumentId: this.documentId.toString(),
        formatId: originalId.toString(),
      }
    );
  }

  private async showConfirmDialogAndOpenEditNewQuotedDocument(title: string, message: string, buttonLabel: string): Promise<void> {
    const result = await this.$commonDialogs.confirmation.confirmCancel(title, [message], buttonLabel);
    if (result === 0) {
      this.editNewQuotedDocument();
    }
  }

  async editNewCopiedDocument(): Promise<void> {
    try {
      const originalId = this.alternativeFormatOriginalId ? this.alternativeFormatOriginalId : this.formatOriginalId;
      const repository = DomainRegistry.format.getFormatRepository();
      const status = await repository.getFormatStatus(originalId);
      if (status.deleted) {
        this.$warn(this.$wording.format.name, "この文書の" + this.$wording.format.name + "は削除されています。");
        return;
      }
      if (status.expired) {
        this.$warn(this.$wording.format.name, "この文書の" + this.$wording.format.name + "は有効期限が切れています。");
        return;
      }
      const reference = await repository.getFormatReference(originalId);
      const isOK = await this.validateFormat(reference, false);
      if (isOK) {
        if (!this.isDocumentReadable) {
          return;
        }
        this.$logger.info("文書をコピー作成します。");

        // 別Windowで開く.(完全SPAにするとメモリリーク発生による問題が顕在化しやすくなる恐れがあるため.)
        this.$openWindow(
          "/EditNewCopiedDocument",
          {
            patientNo: this.patientId,
            copiedDocumentId: this.documentId.toString(),
            isReadRestrictionAlreadyHandled: true.toString(),
          }
        );
      }
    } catch (e) {
      this.$error("エラー", this.$wording.format.name + "の状態確認中にエラーが発生しました。", e);
    }
  }

  async editDocument(): Promise<void> {
    if (!this.isDocumentWritable) {
      return;
    }
    const isScanned = this.$configuration.behavior.collaboration.scan.shouldVerifyBeforeEdit
      ? await this.isScanned()
      : false;
    if (isScanned && !await this.confirmToEdit()) {
      return;
    }
    this.$logger.info("文書を編集します。");

    // 別Windowで開く.(完全SPAにするとメモリリーク発生による問題が顕在化しやすくなる恐れがあるため.)
    this.$openWindow(
      "/EditDocument",
      {
        patientNo: this.patientId,
        documentId: this.documentId.toString(),
        isReadRestrictionAlreadyHandled: true.toString(),
      }
    );
  }

  private async confirmToEdit(): Promise<boolean> {
    this.beginModal();
    try {
      const index = await this.$commonDialogs.confirmation.confirmDangerCancel(
        "編集の確認",
        [this.$configuration.behavior.collaboration.scan.scannedDocumentEditConfirmMessage],
        "編集を開始する"
      );
      return (index === 0);
    } catch (e) {
      return false;
    } finally {
      this.endModal();
    }
  }

  showDocument(): void {
    if (!this.isDocumentReadable) {
      return;
    }
    this.$logger.info("文書を参照します。");

    // 別Windowで開く.(完全SPAにするとメモリリーク発生による問題が顕在化しやすくなる恐れがあるため.)
    this.$openWindow(
      "/ShowDocument",
      {
        patientNo: this.patientId,
        documentId: this.documentId.toString(),
        archiveId: this.archiveId ? this.archiveId.toString() : undefined,
        isReadRestrictionAlreadyHandled: true.toString(),
      }
    );
  }

  editDocumentNote(): void {
    this.$logger.info(this.$wording.document.note + "を編集します。");
    this.$commonDialogs.document.noteInput.show(true, this.editingSource.note, true, this.$wording.document.note)
    .then(d => {
      if (d != null) {
        this.registerDocumentNote(d);
      }
    });
  }

  registerDocumentNote(note: string): void {
    const repository = DomainRegistry.document.getDocumentRepository();
    repository.registerDocumentNote(this.documentId, this.patientId, note)
    .then(i => {
      this.$success(
        "登録完了",
        this.$wording.document.note + "を登録しました。",
      );
      this.writePropertyWhenValid(s => s.note = note);
    }).catch(e => {
      this.$error(
        "登録失敗",
        this.$wording.document.note + "を登録できませんでした。",
        e,
      );
    });
  }

  async deleteDocument(): Promise<void> {
    const isEditing = this.$configuration.behavior.document.editor.conflict.editTokenManagement.isEnable
      ? await this.isEditing()
      : false;
    const isScanned = this.$configuration.behavior.collaboration.scan.shouldVerifyBeforeDelete
      ? await this.isScanned()
      : false;
    const isConfirmed = await this.confirmToDelete(isScanned, isEditing);
    if (!isConfirmed) { return; }

    await this.executeDelete();
  }

  private async isEditing(): Promise<boolean> {
    this.isProcessing = true;
    try {
      const repository = DomainRegistry.document.getDocumentRepository();
      const isEditing = await repository.isEditingDocument(this.patientId, this.documentId);
      // awaitしないでreturnでPromiseをそのまま返すと、一瞬でfinally抜けてしまうので注意
      this.$logger.debug("isEditing: " + isEditing);
      return isEditing;
    } catch (e) {
      this.$warn("文書状態取得失敗", "文書が編集中かどうかを取得できませんでした。", e);
      return false;
    } finally {
      this.isProcessing = false;
    }
  }

  private async isScanned(): Promise<boolean> {
    this.isProcessing = true;
    try {
      const repository = DomainRegistry.collaboration.getCollaborationRepository();
      const isScanned = await repository.isScanned(this.patientId, this.documentId);
      // awaitしないでreturnでPromiseをそのまま返すと、一瞬でfinally抜けてしまうので注意
      this.$logger.debug("isScanned: " + isScanned);
      return isScanned;
    } catch (e) {
      this.$warn("スキャン状態取得失敗", "スキャン取込済みかどうかを取得できませんでした。", e);
      return false;
    } finally {
      this.isProcessing = false;
    }
  }

  private async confirmToDelete(isScanned: boolean, isEditing: boolean): Promise<boolean> {
    this.beginModal();
    try {
      const message = this.getDeleteConfirmMessage(isScanned, isEditing);
      const index = await this.$commonDialogs.confirmation.confirmDeleteCancel(
        "削除の確認",
        [message]
      );
      return (index === 0);
    } catch (e) {
      return false;
    } finally {
      this.endModal();
    }
  }

  private getDeleteConfirmMessage(isScanned: boolean, isEditing: boolean): string {
      if (isScanned && isEditing) {
        return "この文書はスキャン取込済み・編集中ですが、削除してもよろしいですか？";
      }
      if (isScanned) {
        return "この文書はスキャン取込済みですが、削除してもよろしいですか？";
      }
      if (isEditing) {
        return "この文書は編集中ですが、削除してもよろしいですか？";
      }
      return "この文書を削除してもよろしいですか？";
  }

  private async executeDelete(): Promise<void> {
    this.$logger.info("文書を削除します。");
    this.isProcessing = true;
    try {
      const config = this.$configuration.behavior.document.editor.documentAttributes;
      const orderUserCode = (this.$store.getters.orderUser && config.orderUser.shouldUpdateOnEditStart) ? this.$store.getters.orderUser.id : null;
      const attendingUserCode = (this.$store.getters.attendingUser && config.attendingUser.shouldUpdateOnEditStart) ? this.$store.getters.attendingUser.id : null;
      const repository = DomainRegistry.document.getDocumentRepository();
      await repository.deleteDocument(this.documentId, this.patientId, orderUserCode, attendingUserCode);
      await this.launchPostDeletionApplication(this.documentId, this.patientId);
      this.$success(
        "削除完了",
        "文書を削除しました。",
      );
      this.editingSource = null;
    } catch (e) {
      this.$error(
        "削除失敗",
        "文書を削除できませんでした。",
        e,
      );
    } finally {
      this.isProcessing = false;
    }
  }

  private async launchPostDeletionApplication(documentId: number, patientId: string): Promise<void> {
    try {
      const launcher = new PostDeletionApplicationLauncher(this.$configuration.behavior.document.editor.deleter.postDeletion);
      await launcher.launch(this.$store.getters.user, documentId, patientId);
    } catch (e) {
      this.$error("エラー", "文書削除後のアプリケーション起動に失敗しました。", e);
    }
  }

  async printDocument(): Promise<void> {
    this.$logger.info("文書を印刷します。");
    // TODO: hasWatermarkの端末設定（東大では確定登録ではないときに端末設定次第で透かしも印刷させる）
    const launchRequestKey = await this.registerApplicationsLaunchRequest(false);
    this.$printPdf(this.printSource, this.printCopies, false, false, launchRequestKey);
  }

  async selectAndPrintDocument(): Promise<void> {
    this.$logger.info("文書を印刷（選択）します。");
    // TODO: hasWatermarkの端末設定（東大では確定登録ではないときに端末設定次第で透かしも印刷させる）
    const launchRequestKey = await this.registerApplicationsLaunchRequest(true);
    this.$printPdf(this.printSource, this.printCopies, true, false, launchRequestKey);
  }

  private async registerApplicationsLaunchRequest(withPrinterSelection: boolean): Promise<string> {
    try {
      const launcher = new PostPrintingApplicationLauncher(this.$configuration.behavior.document.postPrinting);
      return await launcher.registerApplicationsLaunchRequest(withPrinterSelection, this.$store.getters.user, this.editingSource.patientNo, this.editingSource.id);
    } catch (e) {
      this.$error("印刷後処理", "印刷後の追加処理要求の登録中にエラーが発生しました。", e);
      return null;
    }
  }
}
